/**
 * Styles shared by the Businesses page, the rail switcher and the two
 * Settings panels. Token variables only; class names carry the `biz-` prefix
 * so nothing here can touch legacy selectors.
 */
export const BUSINESS_CSS = `
.biz-page {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.biz-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}
.biz-title {
  margin: 0;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--text);
}
.biz-sub {
  margin: 0;
  font-size: var(--text-base);
  line-height: var(--leading-base);
  color: var(--text-dim);
}
.biz-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}
.biz-toolbar .add-btn,
.biz-actions .add-btn,
.biz-form .add-btn,
.biz-inline .add-btn {
  margin-left: 0;
}
.biz-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.biz-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  flex: 1;
  min-width: 180px;
}
.biz-label {
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--text-dim);
}
.biz-error {
  margin: 0;
  width: 100%;
  font-size: var(--text-sm);
  color: var(--red);
}
.biz-hint {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-dim);
}
.biz-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--space-3);
  list-style: none;
  margin: 0;
  padding: 0;
}
.biz-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.biz-card.is-current {
  border-color: var(--accent);
}
.biz-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-2);
}
.biz-card-name {
  margin: 0;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--text);
  overflow-wrap: anywhere;
}
.biz-badges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  justify-content: flex-end;
}
.biz-figures {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
  margin: 0;
}
.biz-figure dt {
  font-size: var(--text-xs);
  color: var(--text-dim);
}
.biz-figure dd {
  margin: 0;
  font-family: var(--font-mono);
  font-size: var(--text-md);
  color: var(--text);
}
.biz-actions {
  display: flex;
  gap: var(--space-2);
}
.biz-totals {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: var(--space-2);
  margin: 0;
}
.biz-total {
  padding: var(--space-3);
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.biz-total dt {
  font-size: var(--text-xs);
  color: var(--text-dim);
}
.biz-total dd {
  margin: 0;
  font-family: var(--font-mono);
  font-size: var(--text-md);
  color: var(--text);
}
.biz-num {
  text-align: right;
  font-family: var(--font-mono);
}
.biz-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  margin-bottom: var(--space-4);
}
.biz-section h3 {
  margin: 0;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--text);
}
.biz-inline {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2);
}
.biz-stage-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  list-style: none;
  margin: 0;
  padding: 0;
}
.biz-stage {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  background: var(--bg3);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}
.biz-stage-name {
  flex: 1;
  font-size: var(--text-base);
  color: var(--text);
}
.biz-mini {
  padding: var(--space-1) var(--space-2);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-dim);
  font-family: var(--font-ui);
  font-size: var(--text-sm);
  cursor: pointer;
}
.biz-mini:hover:not(:disabled) {
  color: var(--text);
  border-color: var(--border2);
}
.biz-mini:disabled {
  opacity: 0.4;
  cursor: default;
}
.biz-mini:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
.biz-skeletons {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

/* Rail switcher */
.biz-switcher {
  position: relative;
  margin-bottom: var(--space-2);
}
.biz-switcher-btn {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  background: var(--bg3);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text);
  font-family: var(--font-ui);
  font-size: var(--text-base);
  line-height: var(--leading-base);
  text-align: left;
  cursor: pointer;
}
.biz-switcher-btn:hover {
  border-color: var(--border2);
}
.biz-switcher-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}
.biz-switcher-name {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.biz-switcher-letter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  background: var(--accent-glow);
  border: 1px solid var(--accent);
  border-radius: var(--radius-sm);
  color: var(--accent);
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  font-weight: 600;
}
.biz-switcher-btn.is-collapsed {
  justify-content: center;
  padding: var(--space-1);
  background: transparent;
  border-color: transparent;
}
.biz-menu {
  position: fixed;
  z-index: 200;
  min-width: 220px;
  max-width: 280px;
  padding: var(--space-1);
  background: var(--bg2);
  border: 1px solid var(--border2);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}
.biz-menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-dim);
  font-family: var(--font-ui);
  font-size: var(--text-base);
  text-align: left;
  cursor: pointer;
}
.biz-menu-item:hover,
.biz-menu-item:focus-visible {
  background: var(--bg3);
  color: var(--text);
  outline: none;
}
.biz-menu-item.is-current {
  color: var(--text);
  font-weight: 500;
}
.biz-menu-item-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.biz-menu-sep {
  height: 1px;
  margin: var(--space-1) 0;
  background: var(--border);
}
.biz-source {
  margin: 0;
  font-size: var(--text-sm, 12px);
  color: var(--text-dim);
  overflow-wrap: anywhere;
}
.biz-onboard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.biz-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  counter-reset: biz-step;
  font-size: var(--text-xs);
  color: var(--text-muted);
}
.biz-steps li { counter-increment: biz-step; }
.biz-steps li::before { content: counter(biz-step) '. '; }
.biz-steps li.is-done { color: var(--text-dim); text-decoration: none; }
.biz-steps li.is-done::before { content: '✓ '; }
.biz-steps li.is-active { color: var(--text); font-weight: 600; }
`
